import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { achievementById, levelName, LEVEL_NUMERALS } from "core/src/achievements/achievements.js";
import GoldenEggIcon from "../../../shared/GoldenEggIcon";
import musicController from "../../../audio/music-controller";
import styles from "./UnlockCelebration.module.scss";

// The achievements a match unlocked, celebrated in the Match End modal (under
// the rating card, once its own animation has landed). One tile per level
// reached, played one after another:
//   · the tile rises in, the medallion (the level's colour, Bronze I up to
//     Diamond V) STAMPS in with the same ring + sparks as a rank-up
//   · a gleam sweeps the tile, the new level's pip pops
//   · then its eggs fly, one golden egg each, into the "eggs earned" total,
//     which ticks up as they land
// Reduced motion: everything is simply there, no flight.

export interface Unlock {
  id: string;
  level: number;
  eggs: number;
}

// Level colours: the rank tiers' (shared/rating/tiers.ts), I..V.
const LEVEL_COLORS = ["#cd7f32", "#c3ccd9", "#ffcf4d", "#3fe0c8", "#5aa9ff"];
export const levelColor = (level: number) => LEVEL_COLORS[Math.max(0, Math.min(4, level - 1))];

const STEP_MS = 1250; // one tile's whole moment
const FLIGHT_START_MS = 650; // within it: when the eggs take off
const MAX_FLYING = 12; // eggs per tile that actually fly (the count still adds up)

const reducedMotion = () => typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

interface Props {
  unlocks: Unlock[] | null;
  start: boolean; // the rating card has landed: our turn
}

export default function UnlockCelebration({ unlocks, start }: Props) {
  const list = (unlocks || []).filter((u) => achievementById(u.id));
  const still = useRef(reducedMotion()).current;
  const [shown, setShown] = useState(still ? list.length : 0); // tiles revealed so far
  const [earned, setEarned] = useState(still ? list.reduce((s, u) => s + u.eggs, 0) : 0); // the total, as eggs land
  const totalRef = useRef<HTMLSpanElement | null>(null);
  const layerRef = useRef<HTMLDivElement | null>(null);
  const chipRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const timers = useRef<number[]>([]);
  const later = (fn: () => void, ms: number) => { timers.current.push(window.setTimeout(fn, ms)); };
  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  // Play the tiles in turn, starting a beat after the rating card lands (and
  // picking up any that arrive later, from where it got to).
  const played = useRef(0);
  useEffect(() => {
    if (still || !start || played.current >= list.length) return;
    const from = played.current;
    played.current = list.length;
    for (let i = from; i < list.length; i++) {
      const at = 450 + (i - from) * STEP_MS;
      later(() => { setShown(i + 1); musicController.play("achievement"); }, at);
      later(() => flyEggs(i), at + FLIGHT_START_MS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, list.length]);

  // One tile's eggs: small golden eggs arc from its chip to the total; each
  // landing adds one. Many eggs fly as a capped stream, the rest land with the
  // last one.
  const flyEggs = (i: number) => {
    const u = list[i];
    const chip = chipRefs.current[i];
    const total = totalRef.current;
    const layer = layerRef.current;
    if (u && u.eggs) musicController.play("eggs"); // once per tile, as they take off
    if (!u || !chip || !total || !layer || !u.eggs) { setEarned((e) => e + (u ? u.eggs : 0)); return; }
    const base = layer.getBoundingClientRect();
    const a = chip.getBoundingClientRect();
    const b = total.getBoundingClientRect();
    const n = Math.min(u.eggs, MAX_FLYING);
    for (let k = 0; k < n; k++) {
      const egg = document.createElement("i");
      egg.className = styles.flyEgg;
      layer.appendChild(egg);
      const x0 = a.left + a.width / 2 - base.left, y0 = a.top + a.height / 2 - base.top;
      const x1 = b.left + b.width / 2 - base.left, y1 = b.top + b.height / 2 - base.top;
      const lift = 40 + (k % 3) * 14; // the arc's height above the straight line
      const sway = (k % 2 ? 1 : -1) * (6 + (k % 4) * 5);
      const mx = (x0 + x1) / 2 + sway, my = Math.min(y0, y1) - lift;
      const dur = 520 + (k % 3) * 60;
      const delay = k * 70;
      const anim = egg.animate(
        [
          { transform: `translate(${x0}px, ${y0}px) scale(0.4) rotate(-20deg)`, opacity: 0 },
          { transform: `translate(${x0}px, ${y0 - 10}px) scale(1.15) rotate(0deg)`, opacity: 1, offset: 0.15 },
          { transform: `translate(${mx}px, ${my}px) scale(1) rotate(25deg)`, opacity: 1, offset: 0.55 },
          { transform: `translate(${x1}px, ${y1}px) scale(0.55) rotate(40deg)`, opacity: 0.9 },
        ],
        { duration: dur, delay, easing: "cubic-bezier(0.45, 0, 0.55, 1)", fill: "both" },
      );
      const last = k === n - 1;
      anim.onfinish = () => {
        egg.remove();
        setEarned((e) => e + (last ? u.eggs - (n - 1) : 1));
        total.classList.remove(styles.totalBump);
        void total.offsetWidth; // restart the bump
        total.classList.add(styles.totalBump);
      };
    }
  };

  // The section grows smoothly as tiles arrive (no jump in the modal's height).
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [height, setHeight] = useState<number | undefined>(undefined);
  useLayoutEffect(() => {
    if (wrapRef.current) setHeight(wrapRef.current.scrollHeight);
  }, [shown, list.length]);

  if (!list.length) return null;
  const totalEggs = list.reduce((s, u) => s + u.eggs, 0);
  return (
    // Nothing shows (not even the heading, which the sparks' clip margin would
    // otherwise let peek out over the Continue button) until the first tile.
    <section className={`${styles.section} ${shown ? "" : styles.waiting}`} style={{ height: shown ? height : 0 }} aria-label="Achievements unlocked this match">
      <div ref={wrapRef} className={styles.inner}>
        <h4 className={styles.head}>
          <span>{list.length === 1 ? "Achievement unlocked" : `${list.length} achievements unlocked`}</span>
          <span ref={totalRef} className={styles.total} aria-label={`${totalEggs} eggs earned`}>
            +{earned} <GoldenEggIcon size={18} />
          </span>
        </h4>
        <div className={styles.tiles}>
          {list.slice(0, shown).map((u, i) => {
            const a = achievementById(u.id)!;
            const levels = a.levels.length;
            const color = levelColor(levels > 1 ? u.level : 3);
            return (
              <div key={u.id + ":" + u.level} className={`${styles.tile} ${still ? styles.still : ""}`} style={{ "--lv": color } as React.CSSProperties}>
                <span className={styles.gleam} aria-hidden="true" />
                <span className={styles.medal} aria-hidden="true">
                  <span className={styles.medalFace}>{a.icon}</span>
                  {levels > 1 ? <b className={styles.numeral}>{LEVEL_NUMERALS[u.level - 1]}</b> : null}
                  {!still ? (
                    <>
                      <span className={styles.ring} />
                      {Array.from({ length: 10 }, (_, k) => {
                        const ang = (k / 10) * Math.PI * 2;
                        const d = 38 + (k % 3) * 12;
                        return <span key={k} className={styles.spark} style={{ "--dx": Math.cos(ang) * d + "px", "--dy": Math.sin(ang) * d + "px" } as React.CSSProperties} />;
                      })}
                    </>
                  ) : null}
                </span>
                <span className={styles.body}>
                  <span className={styles.eyebrow}>{levels > 1 && u.level > 1 ? `Level ${LEVEL_NUMERALS[u.level - 1]}` : "New achievement"}</span>
                  <span className={styles.name}>{levelName(a, u.level)}</span>
                  <span className={styles.goal}>{a.goal(a.levels[u.level - 1], u.level - 1)}</span>
                  {levels > 1 ? (
                    <span className={styles.pips} aria-label={`Level ${u.level} of ${levels}`}>
                      {Array.from({ length: levels }, (_, k) => (
                        <i key={k} className={k < u.level - 1 ? styles.pipOn : k === u.level - 1 ? styles.pipNew : ""} style={{ "--pip": levelColor(k + 1) } as React.CSSProperties} />
                      ))}
                    </span>
                  ) : null}
                </span>
                <span ref={(el) => { chipRefs.current[i] = el; }} className={styles.reward}>
                  +{u.eggs} <GoldenEggIcon size={16} />
                </span>
              </div>
            );
          })}
        </div>
      </div>
      <div ref={layerRef} className={styles.layer} aria-hidden="true" />
    </section>
  );
}
